import { useCallback, useEffect, useState } from 'react';
import type { HostStateView } from '@quiz/shared';
import { api, ApiError } from '../lib/api.js';
import { useCountdown } from '../lib/usePoll.js';
import type { StaffSession } from '../lib/session.js';
import { Button, Card, Countdown, Banner } from '../components/ui.js';

interface Round {
  id: string;
  title: string;
  index: number;
}

/**
 * The host tab. Everything the person holding the microphone needs and nothing
 * they don't: where we are, the question WITH its answer, how many have answered,
 * a clock, and four big buttons.
 */
export function HostTab({
  staff,
  state,
  offsetMs,
  refresh,
}: {
  staff: StaffSession;
  state: HostStateView;
  offsetMs: number;
  refresh: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const msLeft = useCountdown(state.question?.endsAt ?? null, offsetMs);

  const act = useCallback(
    async (path: string, body: unknown = {}) => {
      setBusy(true);
      setError(null);
      try {
        await api(path, { method: 'POST', token: staff.token, body });
        refresh();
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'That did not work.');
      } finally {
        setBusy(false);
      }
    },
    [staff.token, refresh],
  );

  const q = state.question;
  const canOpen = state.phase === 'CLOSED' && !!q && state.question?.endsAt === null;
  const canClose = state.phase === 'OPEN';
  const canReveal = state.phase === 'CLOSED' && !!q && state.question?.endsAt !== null;
  const canNext = state.phase === 'REVEALED';

  const open = () => q && act('/api/host/open', { questionId: q.id });
  const close = () => q && act('/api/host/close', { questionId: q.id });
  const reveal = () => q && act('/api/host/reveal', { questionId: q.id });
  // expectQuestionId is the compare-and-swap guard: a double-tap on a phone must
  // not silently skip a question.
  const next = () => act('/api/host/next', q ? { expectQuestionId: q.id } : {});

  // The host is holding a microphone, so the common actions have keys too.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === ' ') {
        e.preventDefault();
        if (canOpen) void open();
        else if (canClose) void close();
        else if (canNext) void next();
      }
      if (e.key.toLowerCase() === 'r' && canReveal) void reveal();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // End of the night. The host is about to read the results out, so they get the
  // same standings the room is looking at -- not just the winner's name.
  if (state.phase === 'ENDED') {
    const winner = state.leaderboard[0];
    return (
      <div className="space-y-4">
        <Card className="text-center">
          <p className="text-xs uppercase tracking-wide text-muted">That&rsquo;s the quiz</p>
          {winner ? (
            <>
              <p className="mt-2 text-3xl font-bold text-flag">{winner.callSign}</p>
              <p className="text-sm text-muted">wins with {winner.score} points</p>
            </>
          ) : (
            <p className="mt-2 text-muted">Nobody played.</p>
          )}
        </Card>
        {state.leaderboard.length > 0 && <Standings state={state} title="Final standings" />}

        {/* Practice only. A real night keeps its results and its tickets. */}
        {state.joinMode === 'OPEN' && (
          <div className="space-y-2">
            <Button
              variant="go"
              disabled={busy}
              onClick={() => {
                if (confirm('Clear the players and scores, and run the practice quiz again?')) {
                  void act('/api/host/restart');
                }
              }}
            >
              Run it again
            </Button>
            <p className="text-center text-xs text-muted">
              Wipes the players and scores and goes back to the lobby. Everyone&rsquo;s
              phone returns to the join screen by itself.
            </p>
          </div>
        )}

        <p className="text-center text-xs text-muted">
          {state.playerCount} played.
          {/* Only a manager has the door tab, so only a manager gets told about it. */}
          {staff.role === 'MANAGER' && ' The full breakdown and CSV are on the door tab.'}
        </p>
      </div>
    );
  }

  // Between rounds: a transition card that names what is next, so the host has
  // something to talk over rather than a blank screen.
  if (state.phase === 'LOBBY' || state.phase === 'ROUND_BREAK') {
    return (
      <div className="space-y-4">
        <Card className="text-center">
          <p className="text-xs uppercase tracking-wide text-muted">
            {state.phase === 'LOBBY' ? 'In the lobby' : `End of ${state.round?.title ?? 'the round'}`}
          </p>
          <p className="mt-2 text-3xl font-bold">{state.playerCount} playing</p>
        </Card>

        {state.nextRound ? (
          <Card className="text-center">
            <p className="text-xs uppercase tracking-wide text-muted">Next up</p>
            <p className="mt-1 text-2xl font-semibold text-sky">{state.nextRound.title}</p>
            <div className="mt-4">
              <Button
                variant="go"
                disabled={busy}
                onClick={() => void act('/api/host/round/start', { roundId: (state.nextRound as Round).id })}
              >
                Start round
              </Button>
            </div>
          </Card>
        ) : (
          <Button variant="danger" disabled={busy} onClick={() => void act('/api/host/end')}>
            End the quiz
          </Button>
        )}

        {state.leaderboard.length > 0 && <Standings state={state} />}
        {error && <Banner kind="error">{error}</Banner>}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted">
          {state.round?.title} &middot; Question {(q?.indexInRound ?? 0) + 1} of {q?.totalInRound}
        </span>
        <span className="font-semibold tabular-nums">
          {state.answeredCount} / {state.playerCount} answered
        </span>
      </div>

      <ReadyTally state={state} />

      {q && state.audioMode === 'HOST' && <HostClip state={state} token={staff.token} />}

      <LagPanel token={staff.token} />

      {state.phase === 'OPEN' && msLeft !== null && q?.endsAt && (
        <Countdown msLeft={msLeft} totalMs={q.durationMs} />
      )}

      {q && (
        <Card>
          <p className="text-xl font-semibold leading-snug">{q.prompt}</p>
          <div className="mt-4 rounded-xl bg-go/15 p-3">
            <p className="text-xs uppercase tracking-wide text-muted">Answer</p>
            <p className="text-lg font-bold text-go">{q.correctAnswer}</p>
            {q.explanation && <p className="mt-1 text-sm text-muted">{q.explanation}</p>}
          </div>
          {q.options.length > 0 && (
            <ul className="mt-3 space-y-1 text-sm text-muted">
              {q.options.map((o) => (
                <li key={o} className={o === q.correctAnswer ? 'text-go' : ''}>
                  • {o}
                </li>
              ))}
            </ul>
          )}
          <p className="mt-3 text-xs text-muted">
            {q.pointsValue} points
            {state.round && state.round.pointsMultiplierPct !== 100 &&
              ` (${state.round.pointsMultiplierPct}% round)`}
          </p>
        </Card>
      )}

      {error && <Banner kind="error">{error}</Banner>}

      <div className="grid grid-cols-2 gap-3">
        <Button variant="go" disabled={!canOpen || busy} onClick={() => void open()}>
          Open
        </Button>
        <Button variant="danger" disabled={!canClose || busy} onClick={() => void close()}>
          Close
        </Button>
        <Button disabled={!canReveal || busy} onClick={() => void reveal()}>
          Reveal
        </Button>
        <Button variant="ghost" disabled={!canNext || busy} onClick={() => void next()}>
          Next
        </Button>
      </div>
      <p className="text-center text-xs text-muted">Space advances &middot; R reveals</p>

      {/*
        Getting out mid-quiz. Small text rather than buttons, and both behind a
        confirm: these sit under four large controls the host is tapping in a
        dark room, and neither is something to hit by accident.
      */}
      <div className="flex justify-center gap-6 pt-1 text-xs">
        {state.joinMode === 'OPEN' && (
          <button
            disabled={busy}
            onClick={() =>
              confirm('Start the practice quiz over? Players and scores are cleared.') &&
              void act('/api/host/restart')
            }
            className="text-muted underline disabled:opacity-40"
          >
            Start over
          </button>
        )}
        <button
          disabled={busy}
          onClick={() =>
            confirm('End the quiz now? Everyone sees the final leaderboard.') &&
            void act('/api/host/end')
          }
          className="text-muted underline disabled:opacity-40"
        >
          End the quiz
        </button>
      </div>

      <SubmissionList staff={staff} state={state} refresh={refresh} />
    </div>
  );
}

function Standings({ state, title = 'Standings' }: { state: HostStateView; title?: string }) {
  return (
    <Card>
      <h2 className="mb-2 text-xs uppercase tracking-wide text-muted">{title}</h2>
      <ol className="space-y-1">
        {state.leaderboard.map((r) => (
          <li key={r.playerId} className="flex gap-3">
            <span className="w-6 tabular-nums text-muted">{r.rank}</span>
            <span className="flex-1 truncate">{r.callSign}</span>
            <span className="tabular-nums font-semibold">{r.score}</span>
          </li>
        ))}
      </ol>
    </Card>
  );
}

/** Submissions as they land, with a one-tap regrade on every row. */
function SubmissionList({
  staff,
  state,
  refresh,
}: {
  staff: StaffSession;
  state: HostStateView;
  refresh: () => void;
}) {
  const [pending, setPending] = useState<string | null>(null);
  const [regradeError, setRegradeError] = useState<string | null>(null);

  if (state.submissions.length === 0) {
    return <p className="text-center text-sm text-muted">No answers in yet.</p>;
  }

  async function regrade(submissionId: string, isCorrect: boolean) {
    setPending(submissionId);
    setRegradeError(null);
    try {
      await api('/api/host/regrade', {
        method: 'POST',
        token: staff.token,
        body: { submissionId, isCorrect },
      });
      refresh();
    } catch (err) {
      // The host is standing in front of a room arguing about a spelling. A
      // silent no-op here is the worst possible answer.
      setRegradeError(err instanceof ApiError ? err.message : 'That did not save. Try again.');
    } finally {
      setPending(null);
    }
  }

  return (
    <Card>
      <h2 className="mb-3 text-xs uppercase tracking-wide text-muted">
        Answers ({state.submissions.length})
      </h2>
      {regradeError && (
        <p className="mb-3 rounded-lg bg-stop/15 p-2 text-sm text-stop" role="alert">
          {regradeError}
        </p>
      )}
      <ul className="divide-y divide-ink-3">
        {state.submissions.map((s) => (
          <li key={s.submissionId} className="flex items-center gap-3 py-2">
            <span className="w-24 truncate text-sm">{s.callSign}</span>
            <span className="flex-1 truncate text-sm text-muted">
              {s.didTimeout ? <em>no answer</em> : s.answerText}
              {s.wasGrace && <span className="ml-1 text-flag" title="Inside the grace window">·late</span>}
              {s.wasRegraded && <span className="ml-1 text-sky" title="Regraded by hand">·fixed</span>}
            </span>
            <span className="w-10 text-right text-sm tabular-nums">{s.pointsEarned}</span>
            {/* No threshold is ever right, so every row can be overridden. */}
            <button
              type="button"
              disabled={s.didTimeout || pending === s.submissionId}
              onClick={() => void regrade(s.submissionId, !s.isCorrect)}
              className={`min-h-9 w-9 shrink-0 rounded-lg text-lg font-bold disabled:opacity-30 ${
                s.isCorrect ? 'bg-go/20 text-go' : 'bg-stop/20 text-stop'
              }`}
              title={s.isCorrect ? 'Mark wrong' : 'Mark right'}
            >
              {s.isCorrect ? '✓' : '✗'}
            </button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/**
 * How much of the room can actually SEE the current question.
 *
 * The answered tally says who has decided; this says who can even look. That
 * was a distinction without a difference while every question was text that
 * decrypted in a millisecond, and stops being one the moment a question is a
 * two-megabyte video: opening before the room has it means a chunk of the room
 * misses the clip while the timer runs anyway.
 *
 * Hidden once everyone is in, because a permanent "75 / 75" is a number the
 * host learns to stop reading, and then does not notice the night it says 61.
 */
function ReadyTally({ state }: { state: HostStateView }) {
  const behind = state.playerCount - state.readyCount;
  if (state.playerCount === 0 || behind === 0) return null;

  const pct = Math.round((state.readyCount / state.playerCount) * 100);
  // Amber rather than red: a few phones still loading is the normal state of a
  // freshly staged question, not a fault. It goes red only when most of the
  // room cannot see it, which is when opening would genuinely waste the round.
  const bad = pct < 60;

  return (
    <div className={`rounded-xl p-3 ${bad ? 'bg-stop/15' : 'bg-flag/15'}`}>
      <div className="flex items-baseline justify-between">
        <span className={`text-sm font-semibold ${bad ? 'text-stop' : 'text-flag'}`}>
          {state.readyCount} / {state.playerCount} phones have it loaded
        </span>
        <span className="text-xs text-muted tabular-nums">{pct}%</span>
      </div>
      {state.notReady.length > 0 && (
        <p className="mt-1 text-xs text-muted">
          Still loading: {state.notReady.slice(0, 6).join(', ')}
          {state.notReady.length > 6 ? ` and ${state.notReady.length - 6} more` : ''}
        </p>
      )}
      <p className="mt-1 text-xs text-muted">
        You can open anyway &mdash; they will catch up and see it late.
      </p>
    </div>
  );
}

/**
 * Which phone is always behind.
 *
 * Deliberately not on the state poll and not on screen by default: it is a
 * thing somebody opens when a handset looks wrong, not a number that belongs in
 * front of the host every two seconds. The host screen is operated in a dark
 * room under time pressure, and every permanent number on it is one more thing
 * to read past.
 */
function LagPanel({ token }: { token: string }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<LagRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setError(null);
    try {
      const r = await api<{ players: LagRow[] }>('/api/host/lag', { token });
      setRows(r.players);
    } catch {
      setError('Could not read that just now.');
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => {
          setOpen(true);
          void load();
        }}
        className="text-xs text-muted underline underline-offset-2 hover:text-chalk"
      >
        Which phones are slow?
      </button>
    );
  }

  return (
    <div className="rounded-xl bg-ink-2 p-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold">Load times this event</p>
        <div className="flex gap-3 text-xs">
          <button type="button" onClick={() => void load()} className="text-sky hover:underline">
            Refresh
          </button>
          <button type="button" onClick={() => setOpen(false)} className="text-muted hover:underline">
            Hide
          </button>
        </div>
      </div>

      {error && <p className="mt-2 text-xs text-stop">{error}</p>}

      {rows && rows.length === 0 && (
        <p className="mt-2 text-xs text-muted">
          Nothing recorded yet. Times appear once a question has been staged.
        </p>
      )}

      {rows && rows.length > 0 && (
        <>
          <ul className="mt-2 space-y-1">
            {rows.slice(0, 8).map((r) => (
              <li key={r.callSign} className="flex items-baseline justify-between text-sm">
                <span className="truncate">{r.callSign}</span>
                <span className="ml-3 shrink-0 tabular-nums text-muted">
                  {/* Slow is relative to the room, not to an absolute. Two
                      seconds is fine on 4G and terrible on good wifi, so the
                      colour marks the outliers rather than a fixed threshold. */}
                  <b className={r.medianMs > 4000 ? 'text-stop' : r.medianMs > 2000 ? 'text-flag' : ''}>
                    {(r.medianMs / 1000).toFixed(1)}s
                  </b>{' '}
                  <span className="text-xs">median · {r.questions}q</span>
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-muted">
            Median, so one trip to the bar does not make a working phone look broken.
            Slowest first.
          </p>
        </>
      )}
    </div>
  );
}

interface LagRow {
  callSign: string;
  questions: number;
  medianMs: number;
}

/**
 * The clip, on the host's own device, for a room with a speaker.
 *
 * In HOST audio mode the players' phones were sent no audio at all -- so this
 * IS the audio round. Unsealed, because this tab already shows the correct
 * answer: whoever holds a host PIN is not who the encryption defends against.
 *
 * No autoplay. The host decides when the room is listening, and a clip that
 * started itself while somebody was still reading the question aloud would be
 * worse than useless.
 */
function HostClip({ state, token }: { state: HostStateView; token: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const questionId = state.question?.id;

  useEffect(() => {
    if (!questionId) return;
    let revoke: string | null = null;
    let cancelled = false;
    void fetch(`/api/host/media/${questionId}`, { headers: { authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.blob() : null))
      .then((b) => {
        if (!b || cancelled) return;
        revoke = URL.createObjectURL(b);
        setUrl(revoke);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
      setUrl(null);
    };
  }, [questionId, token]);

  if (!url) return null;

  return (
    <div className="rounded-xl bg-sky/10 p-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-sky">
        Play this aloud &mdash; the room&rsquo;s phones have no audio
      </p>
      <audio src={url} controls className="mt-2 w-full" />
    </div>
  );
}
